import gsap from 'gsap';
import { Container, FillGradient, Graphics, Point, Text } from 'pixi.js';
import { Reel } from './Reel.js';
import { gameConfig } from '../config/gameConfig.js';
import { isPrimaryPointerAction } from '../utils/pointerPrimary.js';
import { createExpandingWildSpineInstance, fitExpandingWildMultiplierToReel, holdExpandingWildSpineAtEnd, playExpandingWildSpineAnimation, resetExpandingWildSpine, resolveExpandingWildSpineAnimation } from '../rendering/spineLoader.js';
import { sound } from '../utils/soundManager.js';
import { AnticipationColumnFx, computeAnticipationProfile, getAnticipationTeaseColumn, getQualifyingTeaseSymbolIds } from '../effects/reelAnticipation.js';
import { animatePaylineEnergy } from '../effects/paylineEnergy.js';
import { attachLuxuryGoldFrameFx } from '../effects/luxuryGoldFx.js';

export class ReelContainer {
  /**
   * @param {{
   *   spineAssets: Record<number, any>,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   * }} opts
   */
  constructor(opts) {
    this.spineAssets = opts.spineAssets;
    this.animationController = opts.animationController;
    /** True after 2+ same-type scatters have landed and motion tease was armed this spin. */
    this._motionAnticipationArmed = false;
    this.root = new Container();
    this.root.sortableChildren = true;

    /** Reel strips — stays below cabinet frame (see GameApp frame zIndex 1). */
    this.reelsLayer = new Container();
    this.reelsLayer.zIndex = 0.5;
    this.root.addChild(this.reelsLayer);

    /**
     * Expanding-wild FX — reel-block coordinates, moves with {@link GameApp#_gameVisualLayer}.
     * zIndex 11: above cabinet logo (10), below canvas controls / modals (15+).
     */
    this.expandingWildFxLayer = new Container();
    this.expandingWildFxLayer.eventMode = 'none';
    this.expandingWildFxLayer.zIndex = 11;
    this.root.addChild(this.expandingWildFxLayer);

    /** Winning cells only: reparented here so fruit + win-frame Spine draw above the cabinet frame. */
    this._symbolWinLiftLayer = new Container();
    this._symbolWinLiftLayer.eventMode = 'none';
    this._symbolWinLiftLayer.zIndex = 2;
    this.root.addChild(this._symbolWinLiftLayer);
    this.reelsMaskShape = new Graphics();
    this._maskPadRelaxed = Math.max(0, (gameConfig.ui.wellInset ?? 0) + 18);
    this._maskInsetTopBottom = 10;

    /** @type {Reel[]} */
    this.reels = [];
    const step = gameConfig.cellWidth + gameConfig.reelGap;
    for (let i = 0; i < gameConfig.reelCount; i++) {
      const reel = new Reel({
        index: i,
        spineAssets: this.spineAssets,
        animationController: this.animationController,
      });
      reel.root.x = i * step;
      reel.root.zIndex = 2;
      this.reelsLayer.addChild(reel.root);
      this.reels.push(reel);
    }
    this._applyReelsViewportMask(this._maskPadRelaxed);

    /** Payline guides — above lifted win symbols, below win amount overlay. */
    this.allLinesOverlay = new Container();
    this.allLinesOverlay.eventMode = 'passive';
    this.allLinesOverlay.zIndex = 3;
    this.root.addChild(this.allLinesOverlay);
    this._selectedGuideLine = null;

    this.winOverlay = new Container();
    this.winOverlay.eventMode = 'none';
    this.winOverlay.zIndex = 4;
    this.root.addChild(this.winOverlay);
    this._renderAllPaylineGuides();

    /** @type {import('gsap').core.Tween | null} */
    this._winLineCycleDelay = null;
    /** @type {(() => void) | null} */
    this._winLineCycleResolve = null;
    this._winLineCycleActive = false;
    this._stopRequested = false;

    this._anticipationFx = new AnticipationColumnFx({
      parent: this.reelsLayer,
      animationController: this.animationController,
    });
    /** @type {import('../effects/cabinetJuice.js').CabinetJuice | null} */
    this._cabinetJuice = null;
    /** @type {Graphics | null} */
    this._reelWellDim = null;
    /** @type {import('../effects/reelAnticipation.js').ReturnType<computeAnticipationProfile> | null} */
    this._lastAnticipationProfile = null;
    /** Persisted expanding-wild hold FX for photo / debug. */
    this._expandingWildHoldRoots = [];
    /** Per-reel buy-bonus multiplier (0, or 2/3/5/10) for visible wild cells. */
    this._wildReelMultipliers = null;
    /** Per-reel expanding-wild symbol id that the multiplier badge belongs to. */
    this._wildReelSymbolIds = null;
  }

  /**
   * @param {import('../effects/cabinetJuice.js').CabinetJuice | null} juice
   */
  setCabinetJuice(juice) {
    this._cabinetJuice = juice;
  }

  /** Hide payline reel-well dim (e.g. during big-win overlay). */
  clearReelWellDim() {
    this._setReelWellDim(false);
  }

  clearStopRequest() {
    this._stopRequested = false;
  }

  /**
   * @param {number} [pad]
   * @param {boolean} [idleStopped] When true, tighten top mask to hide the cell above the window (idle only).
   */
  _applyReelsViewportMask(pad = this._maskPadRelaxed, idleStopped = true) {
    const safePad = Math.max(0, Number(pad) || 0);
    const baseInsetTop = Math.max(0, Number(this._maskInsetTopBottom) || 0);
    const idleTopExtra =
      idleStopped && Math.max(0, Number(gameConfig.ui.reelViewportMaskTopExtraWhenIdle) || 0);
    const insetTop = baseInsetTop + idleTopExtra;
    const bottomExtra = Math.max(0, Number(gameConfig.ui.reelViewportMaskBottomExtra) || 0);
    const insetBottom = baseInsetTop + bottomExtra;
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;

    this.reelsMaskShape.clear();
    this.reelsMaskShape
      .rect(
        -safePad,
        -safePad + insetTop,
        reelBlockW + safePad * 2,
        reelBlockH + safePad * 2 - insetTop - insetBottom,
      )
      .fill(0xffffff);
    const maskHost = this._reelsMaskHost ?? this.root;
    if (this.reelsMaskShape.parent !== maskHost) maskHost.addChild(this.reelsMaskShape);
    this.reelsLayer.mask = this.reelsMaskShape;
  }

  setPaylineGuidesVisible(visible) {
    this.allLinesOverlay.visible = !!visible;
  }

  /**
   * @param {import('./Symbol.js').Symbol} sym
   * @param {import('./Reel.js').Reel} reel
   */
  _liftSymbolForWin(sym, reel) {
    if (!sym?.root || sym.root.parent === this._symbolWinLiftLayer) return;
    const g = sym.root.getGlobalPosition(new Point());
    reel.stripContainer.removeChild(sym.root);
    this._symbolWinLiftLayer.addChild(sym.root);
    const lp = this._symbolWinLiftLayer.toLocal(g);
    sym.root.position.set(lp.x, lp.y);
  }

  /**
   * @param {import('./Symbol.js').Symbol} sym
   * @param {import('./Reel.js').Reel} reel
   */
  _lowerSymbolFromWin(sym, reel) {
    if (!sym?.root || sym.root.parent !== this._symbolWinLiftLayer) return;
    const g = sym.root.getGlobalPosition(new Point());
    this._symbolWinLiftLayer.removeChild(sym.root);
    reel.stripContainer.addChild(sym.root);
    const lp = reel.stripContainer.toLocal(g);
    sym.root.position.set(lp.x, lp.y);
  }

  /**
   * @param {{ reel: number, row: number }} cell
   * @returns {{ x: number, y: number }}
   */
  _cellCenter(cell) {
    const step = gameConfig.cellWidth + gameConfig.reelGap;
    const cw = gameConfig.cellWidth;
    const ch = gameConfig.cellHeight;
    return {
      x: cell.reel * step + cw / 2,
      y: cell.row * ch + ch / 2,
    };
  }

  /**
   * Full payline path (every reel), for overlay — not limited to winning run length.
   * @param {number} paylineIndex
   * @returns {{ x: number, y: number }[]}
   */
  _fullPaylinePathPoints(paylineIndex) {
    const def = gameConfig.paylines[paylineIndex];
    if (!def?.length) return [];
    return def.map((row, reel) => this._cellCenter({ reel, row }));
  }

  /**
   * Every cell on a payline (all reels), not just the winning run.
   * @param {number} paylineIndex
   * @returns {import('../logic/winEvaluator.js').CellRef[]}
   */
  _fullPaylineCells(paylineIndex) {
    const def = gameConfig.paylines[paylineIndex];
    if (!def?.length) return [];
    return def.slice(0, gameConfig.reelCount).map((row, reel) => ({ reel, row }));
  }

  _paylineColor(paylineIndex) {
    const colors =
      gameConfig.theme.winLineBorderColors ??
      gameConfig.theme.paylineColors ??
      [gameConfig.theme.winGlow];
    if (paylineIndex == null || !Number.isFinite(paylineIndex)) return colors[0];
    return colors[paylineIndex % colors.length];
  }

  clearPaylineOverlay() {
    gsap.killTweensOf(this.winOverlay);
    for (const child of this.winOverlay.children) {
      const energyTw = child.__energyTween;
      if (energyTw) {
        try {
          energyTw.kill();
        } catch (_e) {
          /* ignore */
        }
        this.animationController.releaseTween(energyTw);
        child.__energyTween = null;
      }
    }
    const removed = this.winOverlay.removeChildren();
    for (const child of removed) {
      child.destroy({ children: true });
    }
    this.winOverlay.alpha = 1;
    this._setReelWellDim(false);
  }

  _renderAllPaylineGuides() {
    const prev = this.allLinesOverlay.removeChildren();
    for (const child of prev) {
      child.destroy({ children: true });
    }
    const t = gameConfig.theme;
    const colors = t.paylineColors ?? [t.winGlow, t.spinRing, t.goldBright];
    for (let i = 0; i < gameConfig.paylines.length; i++) {
      if (this._selectedGuideLine != null && this._selectedGuideLine !== i) continue;
      const basePts = this._fullPaylinePathPoints(i);
      const pts = this._offsetGuidePath(basePts, i);
      if (pts.length < 2) continue;
      const lineColor = colors[i % colors.length];
      const g = new Graphics();
      if (this._selectedGuideLine === i) {
        g.moveTo(pts[0].x, pts[0].y);
        for (let p = 1; p < pts.length; p++) g.lineTo(pts[p].x, pts[p].y);
        g.stroke({
          width: 12,
          color: 0xffffff,
          alpha: 0.32,
          cap: 'round',
          join: 'round',
        });
      }
      g.moveTo(pts[0].x, pts[0].y);
      for (let p = 1; p < pts.length; p++) g.lineTo(pts[p].x, pts[p].y);
      g.stroke({
        width: this._selectedGuideLine === i ? 9 : 7,
        color: lineColor,
        alpha: this._selectedGuideLine === i ? 1 : 0.72,
        cap: 'round',
        join: 'round',
      });
      const badge = new Container();
      const badgeBg = new Graphics();
      const badgeW = 54;
      const badgeH = 46;
      badgeBg.roundRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, 14).fill({
        color: 0x020617,
        alpha: 0.86,
      });
      badgeBg.roundRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, 14).stroke({
        width: 3,
        color: lineColor,
        alpha: 0.95,
      });
      const label = new Text({
        text: String(i + 1),
        style: {
          fontFamily: t.fontUi,
          fontSize: 30,
          fontWeight: '900',
          fill: 0xffffff,
          stroke: { color: 0x030712, width: 5 },
        },
      });
      label.anchor.set(0.5, 0.5);
      label.y = 1;
      badge.addChild(badgeBg, label);

      const topY = this._cellCenter({ reel: 0, row: 0 }).y;
      const bottomY = this._cellCenter({ reel: 0, row: 2 }).y;
      const stepY = (bottomY - topY) / 4;
      if (i < 5) {
        badge.x = pts[0].x - 56;
        badge.y = topY + i * stepY;
      } else {
        badge.x = pts[pts.length - 1].x + 56;
        badge.y = topY + (i - 5) * stepY;
      }

      badge.alpha = 1;
      badge.eventMode = 'static';
      badge.cursor = 'pointer';
      badge.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        this._selectedGuideLine = this._selectedGuideLine === i ? null : i;
        this._renderAllPaylineGuides();
      });
      this.allLinesOverlay.addChild(g, badge);
    }
    if (this.allLinesOverlay.parent === this.root) {
      this.root.setChildIndex(this.allLinesOverlay, this.root.children.length - 2);
    }
  }

  _offsetGuidePath(points, lineIndex) {
    if (!Array.isArray(points) || points.length < 2) return points;
    const out = [];
    const group = Math.floor(lineIndex / 2);
    const sign = lineIndex % 2 === 0 ? -1 : 1;
    const mag = Math.min(16, 3 + group * 2);
    const first = points[0];
    const last = points[points.length - 1];
    const dx = last.x - first.x;
    const dy = last.y - first.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = (-dy / len) * sign * mag;
    const ny = (dx / len) * sign * mag;
    for (const p of points) {
      out.push({ x: p.x + nx, y: p.y + ny });
    }
    return out;
  }

  /**
   * @param {import('../logic/winEvaluator.js').WinResult['hits']} hits
   * @param {{ animateEntry?: boolean }} [opts]
   * Overlay path uses the full payline (all reels); win highlights use each hit’s `cells` only.
   */
  _renderPaylineOverlayHits(hits, opts = {}) {
    const animateEntry = opts.animateEntry !== false;
    this.clearPaylineOverlay();
    if (!hits.length) return;

    const t = gameConfig.theme;
    const u = gameConfig.ui;

    let renderedLines = 0;
    hits.forEach((hit, hitIndex) => {
      if (hit?.displayMode === 'symbols') return;
      const fullLineCells =
        hit.paylineIndex != null ? this._fullPaylineCells(hit.paylineIndex) : hit.cells ?? [];
      const pts =
        fullLineCells.length > 1
          ? fullLineCells.map((c) => this._cellCenter(c))
          : this._fullPaylinePathPoints(hit.paylineIndex);
      if (pts.length < 2) return;
      renderedLines++;

      const lineColor = this._paylineColor(hit.paylineIndex);
      const lineRoot = new Container();
      lineRoot.eventMode = 'none';

      const glow = new Graphics();
      glow.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) glow.lineTo(pts[i].x, pts[i].y);
      glow.stroke({
        width: u.winLineGlowWidth,
        color: lineColor,
        alpha: 0.28,
        cap: 'round',
        join: 'round',
      });

      const core = new Graphics();
      core.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) core.lineTo(pts[i].x, pts[i].y);
      core.stroke({
        width: u.winLineCoreWidth,
        color: lineColor,
        alpha: 0.95,
        cap: 'round',
        join: 'round',
      });

      const joints = new Graphics();
      for (const p of pts) {
        joints.circle(p.x, p.y, u.winLineJointRadius).fill({ color: lineColor, alpha: 0.85 });
      }

      lineRoot.addChild(glow, core, joints);
      lineRoot.__energyPts = pts;

      const mx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const my = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      const amt = new Text({
        text: hit.payout.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        style: {
          fontFamily: t.fontUi,
          fontSize: 22,
          fontWeight: '800',
          fill: lineColor,
          dropShadow: {
            alpha: 0.85,
            angle: Math.PI / 2,
            blur: 4,
            color: gameConfig.theme.panelDeep,
            distance: 1,
          },
        },
      });
      amt.anchor.set(0.5, 0.5);
      amt.x = mx;
      amt.y = my - u.winLineLabelOffsetY - hitIndex * 26;
      lineRoot.addChild(amt);

      this.winOverlay.addChild(lineRoot);
    });

    if (renderedLines === 0) return;

    if (this.winOverlay.parent === this.root) {
      this.root.setChildIndex(this.winOverlay, this.root.children.length - 1);
    }

    if (animateEntry) {
      this.winOverlay.alpha = 0;
      this.animationController.runGsap(this.winOverlay, {
        alpha: 1,
        duration: 0.32,
        ease: 'power2.out',
        onComplete: () => {
          this.animationController.runGsap(this.winOverlay, {
            alpha: 0.88,
            duration: 0.85,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      });
    } else {
      this.winOverlay.alpha = 1;
    }
  }

  /**
   * Draw paylines through winning cells and show per-line win amounts.
   * @param {import('../logic/winEvaluator.js').WinResult['hits']} hits
   */
  showPaylineWins(hits) {
    if (!hits?.length) return;
    if (hits.length === 1) {
      void this._presentWinLinePhase(hits[0]);
      return;
    }
    this._renderPaylineOverlayHits(hits, { animateEntry: true });
    this._setReelWellDim(true);
    this._playWinLineSound();
    this._applyGridWinPresentationForHits(hits);
    const cells = this._uniqueWinCells(hits.flatMap((h) => h.cells));
    this._startWinClips(cells, { staggerSec: 0 });
  }

  /**
   * Draw one payline, highlight winners, and start symbol win clips.
   * Every line uses the same timing: all winning symbols start together; energy trail is non-blocking.
   * @param {import('../logic/winEvaluator.js').WinResult['hits'][number]} hit
   * @param {{ onPhase?: (hit: import('../logic/winEvaluator.js').WinResult['hits'][number]) => void, totalWinAmount?: number }} [opts]
   */
  async _presentWinLinePhase(hit, opts = {}) {
    const lineCells = hit.cells ?? [];
    const clipOpts = { staggerSec: 0 };

    this._renderPaylineOverlayHits([hit], { animateEntry: false });
    this._setReelWellDim(true);
    this._playWinLineSound();
    this._applyGridWinPresentation(lineCells, {
      paylineIndex: hit.paylineIndex,
      displayMode: hit.displayMode,
    });
    opts.onPhase?.(opts.totalWinAmount, hit);

    void this._runPaylineEnergyForCurrentOverlay();
    await this._playLineWinClips(lineCells, clipOpts);
  }

  _playWinLineSound() {
    if (this._suppressWinLineSound) return;
    sound.play('winFrame', { allowOverlap: true });
  }

  /** Re-enable payline SFX after big-win tier audio finishes. */
  setWinLineSoundSuppressed(suppress) {
    const wasSuppressed = !!this._suppressWinLineSound;
    this._suppressWinLineSound = !!suppress;
    if (wasSuppressed && !suppress && this._winLineCycleActive) {
      this._playWinLineSound();
    }
  }

  async _runPaylineEnergyForCurrentOverlay(durationSec) {
    const children = this.winOverlay.children;
    if (!children.length) return;
    const lineRoot = children[children.length - 1];
    const pts = lineRoot?.__energyPts;
    if (!Array.isArray(pts) || pts.length < 2) return;
    await animatePaylineEnergy(lineRoot, pts, this.animationController, durationSec);
  }

  /**
   * @param {boolean} on
   */
  _setReelWellDim(on) {
    const fx = gameConfig.visualFx ?? {};
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    if (!on) {
      if (this._reelWellDim?.parent) {
        this._reelWellDim.parent.removeChild(this._reelWellDim);
        this._reelWellDim.destroy();
        this._reelWellDim = null;
      }
      return;
    }
    if (this._reelWellDim) return;
    const g = new Graphics();
    g.rect(0, 0, reelBlockW, reelBlockH).fill({
      color: gameConfig.theme?.panelDeep ?? 0x0a0604,
      alpha: fx.reelWellDimAlpha ?? 0.38,
    });
    g.eventMode = 'none';
    g.zIndex = 0.41;
    this.reelsLayer.addChild(g);
    this._reelWellDim = g;
  }

  /**
   * Per-reel landing visuals: anticipation column, symbol pops, scatter tease glow.
   * @param {number} col
   * @param {number[][]} grid
   * @param {{ fastStop?: boolean }} [opts]
   */
  handleReelLandedVisuals(col, grid, opts = {}) {
    if (opts.fastStop || !Array.isArray(grid)) {
      this._anticipationFx.hideAll();
      return;
    }

    const fx = gameConfig.visualFx ?? {};
    const popIds = fx.landPopSymbolIds ?? [9, 10, 11];
    const teaseSymbolIds = getQualifyingTeaseSymbolIds(grid, col);

    for (let c = 0; c <= col; c++) {
      const landedReel = this.reels[c];
      const landedSyms = grid[c];
      if (!landedReel || !Array.isArray(landedSyms)) continue;
      for (let row = 0; row < gameConfig.rowCount; row++) {
        const sym = landedReel.getSymbolAtVisibleRow(row);
        if (!sym) continue;
        const sid = landedSyms[row];
        if (c === col && popIds.includes(sid)) {
          sym.playLandPop(landedReel.stripContainer);
        }
        sym.setScatterTeaseGlow(teaseSymbolIds.includes(sid));
      }
    }

    this._anticipationFx.hideAll();
    const teaseCol = getAnticipationTeaseColumn(grid, col);
    if (teaseCol >= 0) {
      this._anticipationFx.showColumn(teaseCol);
    }

    // Slow remaining reels only after the 2nd same-type scatter has landed — never from spin start.
    if (!this._motionAnticipationArmed) {
      const profile = computeAnticipationProfile(grid, false, col);
      if (profile.startCol >= 0) {
        this._motionAnticipationArmed = true;
        this._lastAnticipationProfile = profile;
        const slow = Math.max(1, Number(fx.anticipationCruiseSlowFactor) || 1.55);
        for (let c = 0; c < this.reels.length; c++) {
          if (!profile.anticipated[c]) continue;
          this.reels[c].applyAnticipationTease({
            extraCells: profile.extraCells[c],
            slowFactor: slow,
          });
        }
      }
    }
  }

  clearAnticipationVisuals() {
    this._anticipationFx.hideAll();
    this._lastAnticipationProfile = null;
    this._motionAnticipationArmed = false;
    for (const reel of this.reels) {
      for (let r = 0; r < gameConfig.rowCount; r++) {
        reel.getSymbolAtVisibleRow(r)?.setScatterTeaseGlow(false);
      }
    }
  }

  stopWinLineCycle() {
    this._winLineCycleActive = false;
    if (this._winLineCycleDelay) {
      const tw = this._winLineCycleDelay;
      this._winLineCycleDelay = null;
      tw.kill();
      this.animationController.releaseTween(tw);
    }
    if (this._winLineCycleResolve) {
      const r = this._winLineCycleResolve;
      this._winLineCycleResolve = null;
      r();
    }
    this._setReelWellDim(false);
  }

  /**
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   * @returns {import('../logic/winEvaluator.js').CellRef[]}
   */
  _uniqueWinCells(cells) {
    const seen = new Set();
    const out = [];
    for (const c of cells) {
      const k = `${c.reel},${c.row}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
    }
    return out;
  }

  /**
   * Seconds to wait for one payline’s win clips (longest symbol clip; optional per-cell stagger).
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   */
  _lineWinPlaybackSec(cells, opts = {}) {
    const cfg = gameConfig.adapter;
    const minHold =
      cfg.winLinePhaseHoldSec ?? gameConfig.winAnimationMinDisplaySec ?? 3;
    const stagger =
      opts.staggerSec != null && Number.isFinite(opts.staggerSec)
        ? opts.staggerSec
        : (cfg.winStaggerSec ?? 0);
    const unique = this._uniqueWinCells(cells);
    if (!unique.length) return minHold;

    let maxDur = 0;
    for (const c of unique) {
      const sym = this.reels[c.reel]?.getSymbolAtVisibleRow(c.row);
      maxDur = Math.max(maxDur, sym?.getWinClipDurationSec?.() ?? 0);
    }
    const staggerPad = unique.length >= 2 ? (unique.length - 1) * stagger : 0;
    return Math.max(minHold, maxDur + staggerPad + 0.05);
  }

  /**
   * Cycles one winning payline at a time (line 1 → line 2 → … → repeat) until {@link prepareForNewRound}.
   * @param {import('../logic/winEvaluator.js').WinResult['hits']} hitsSorted sorted by `paylineIndex`
   * @param {number} totalWinAmount
   * @param {(totalWin: number, hit: import('../logic/winEvaluator.js').WinResult['hits'][number]) => void} onPhase
   * @param {{ suppressLineSound?: boolean }} [opts]
   */
  startWinLineCycle(hitsSorted, totalWinAmount, onPhase, opts = {}) {
    this.stopWinLineCycle();
    if (!hitsSorted.length) return;

    this._suppressWinLineSound = !!opts.suppressLineSound;

    const n = hitsSorted.length;
    this._winLineCycleActive = true;

    const runPhase = async (index) => {
      while (this._winLineCycleActive) {
        const hit = hitsSorted[index];
        await this._presentWinLinePhase(hit, {
          totalWinAmount,
          onPhase,
        });
        if (!this._winLineCycleActive) return;
        index = (index + 1) % n;
      }
    };

    void runPhase(0);
  }

  /**
   * Ensure visible cells are idle (error cleanup / no-matrix path).
   * Never starts Spine spinning/blur — cruise is idle-only (Blazing Sevens).
   * @param {boolean} spinning Ignored when true; when false, clears any leftover spin flags.
   */
  setAllVisibleSymbolsSpinning(spinning) {
    if (spinning) return;
    for (const reel of this.reels) {
      for (let r = 0; r < gameConfig.rowCount; r++) {
        const sym = reel.getSymbolAtVisibleRow(r);
        if (sym) sym.clearSpinPresentation();
      }
    }
  }

  /**
   * @param {number[][]} grid column-major [reel][row]
   */
  beginPendingSpin() {
    this.clearStopRequest();
    this._applyReelsViewportMask(this._maskPadRelaxed, false);
    this.reels.forEach((reel) => reel.beginPendingSpin());
  }

  /**
   * @param {{ instant?: boolean, landTimeScale?: number, staggerFrames?: number }} [opts]
   *   `staggerFrames` — delay each next reel’s stop by N frames (~16ms) so Android
   *   never settles all five Spine strips on the same tick.
   */
  requestStop(opts = {}) {
    const staggerFrames = Math.max(0, Math.floor(Number(opts.staggerFrames) || 0));
    if (staggerFrames <= 0 || this.reels.length <= 1) {
      this.reels.forEach((reel) => reel.requestStop(opts));
      return;
    }
    const { staggerFrames: _sf, ...stopOpts } = opts;
    this.reels.forEach((reel, i) => {
      if (i === 0) {
        reel.requestStop(stopOpts);
        return;
      }
      const delayMs = i * staggerFrames * (1000 / 60);
      setTimeout(() => {
        try {
          reel.requestStop(stopOpts);
        } catch (_e) {
          /* ignore */
        }
      }, delayMs);
    });
  }

  /**
   * @param {number[][]} grid column-major [reel][row]
   * @param {{ reelStopStaggerScale?: number, turboMode?: boolean, liteScrollBlur?: boolean, onReelLanded?: (col: number) => void }} [opts]
   *   `reelStopStaggerScale` multiplies `reelStopStaggerSec` (only stop spacing, not scroll speed).
   *   `turboMode` — dedicated short Quick Spin timeline (no wait+seek).
   *   Idle-only cruise always — `liteScrollBlur` is ignored.
   *   `onReelLanded(col)` fires once per reel at stop impact (overshoot), not after bounce settle.
   */
  async spinToGrid(grid, opts = {}) {
    this.clearStopRequest();
    const { reelStopStaggerScale, turboMode, onReelLanded } = opts;
    this.clearAnticipationVisuals();

    /** One random strip depth per spin — keeps reel-to-reel stop gaps aligned with stagger timing. */
    let landingOffset;
    let extraCells;
    if (turboMode) {
      landingOffset =
        (gameConfig.turboStripLandingOffsetMin ?? 4) +
        Math.floor(Math.random() * Math.max(1, gameConfig.turboStripLandingOffsetSpread ?? 2));
      extraCells =
        (gameConfig.turboExtraSpinCellsMin ?? 3) +
        Math.floor(Math.random() * Math.max(1, gameConfig.turboExtraSpinCellsSpread ?? 2));
    } else {
      landingOffset =
        gameConfig.stripLandingOffsetMin +
        Math.floor(Math.random() * gameConfig.stripLandingOffsetSpread);
      extraCells =
        gameConfig.extraSpinCellsMin + Math.floor(Math.random() * gameConfig.extraSpinCellsSpread);
    }
    const travelBase = { landingOffset, extraCells };

    const tasks = this.reels.map((reel, col) => {
      const reelCol = grid[col];
      const colResult = Array.from(
        { length: gameConfig.rowCount },
        (_, row) => reelCol?.[row],
      );
      // Anticipation slowdown is applied mid-spin in handleReelLandedVisuals after 2 scatters land.
      // Idle-only cruise for every tier (Blazing Sevens) — motion provides the blur read.
      return reel.spinToResult(colResult, {
        columnIndex: col,
        fastStop: false,
        turboMode: !!turboMode,
        liteScrollBlur: true,
        reelStopStaggerScale,
        travelBase,
        onLanded: () => {
          this._syncWildMultiplierBadges({ col, landing: true });
          if (typeof onReelLanded !== 'function') return;
          try {
            onReelLanded(col);
          } catch (_e) {
            /* never let SFX break the spin */
          }
        },
        onSettled: () => {
          this._syncWildMultiplierBadges({ col });
        },
      });
    });
    sound.play('reelSpin');
    await Promise.all(tasks);
    sound.stopReelSpinSound();
    this.clearAnticipationVisuals();
    // After landing: tight top again so idle strip does not peek above row 0.
    this._applyReelsViewportMask(this._maskPadRelaxed, true);
  }

  /**
   * Full-column gold backdrop behind expanding-wild spine / spritesheet.
   * Single full-size panel — one gradient fill + rim on one Graphics object.
   * @param {number} cw
   * @param {number} blockH
   */
  _createExpandingWildColumnBg(cw, blockH, { pulse = true } = {}) {
    const cfg = gameConfig.adapter;
    const t = gameConfig.theme;
    const bgW = cw;
    const bgH = blockH;
    const bgR = cfg.expandingWildColumnBgRadius ?? 12;
    const hi = t.expandingWildColumnBgHi ?? t.winGlow;
    const mid = t.expandingWildColumnBgMid ?? t.modalAccent;
    const lo = t.expandingWildColumnBg ?? t.panelDeep;
    const bloom = t.goldBright ?? 0xfff8dc;
    const x0 = -bgW / 2;
    const y0 = -bgH / 2;

    const bg = new Graphics();
    bg.eventMode = 'none';
    const grad = new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.32 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 1.08,
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: bloom },
        { offset: 0.18, color: hi },
        { offset: 0.42, color: mid },
        { offset: 0.72, color: lo },
        { offset: 1, color: mid },
      ],
    });
    bg.roundRect(x0, y0, bgW, bgH, bgR).fill(grad);
    bg.roundRect(x0, y0, bgW, bgH, bgR).stroke({
      width: 2.5,
      color: hi,
      alpha: 0.85,
    });

    const layer = new Container();
    layer.eventMode = 'none';
    layer.addChild(bg);
    layer.alpha = cfg.expandingWildColumnBgAlpha ?? 0.88;

    let pulseTween = null;
    if (pulse) {
      const baseAlpha = layer.alpha;
      const swing = cfg.expandingWildColumnBgPulseSwing ?? 0.22;
      pulseTween = gsap.to(layer, {
        alpha: Math.min(1, baseAlpha + swing),
        duration: cfg.expandingWildColumnBgPulseSec ?? 0.9,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
      this.animationController.trackTween(pulseTween);
    }

    return { layer, pulseTween, grad };
  }

  /**
   * @param {import('pixi.js').Container} columnRoot
   * @param {number} cw
   * @param {number} blockH
   */
  _attachExpandingWildLuxuryFx(columnRoot, cw, blockH) {
    const bgR = gameConfig.adapter.expandingWildColumnBgRadius ?? 12;
    return attachLuxuryGoldFrameFx(
      columnRoot,
      { x: -cw / 2, y: -blockH / 2, w: cw, h: blockH, r: bgR },
      this.animationController,
      { loop: true },
    );
  }

  _clearExpandingWildHoldFx() {
    for (const col of this._expandingWildHoldRoots) {
      col.bgPulseTween?.kill();
      col.luxuryFx?.stop();
      col.restoreVisibility?.();
      if (col.columnRoot?.parent) {
        col.columnRoot.parent.removeChild(col.columnRoot);
        col.columnRoot.destroy({ children: true });
      }
      col.columnBgGrad?.destroy?.();
    }
    this._expandingWildHoldRoots = [];
  }

  /**
   * Show expanding-wild columns frozen on the last animation frame (photo / debug).
   * @param {number[]} expandingWild per reel, 0 = none; >0 = wild symbol id
   */
  showExpandingWildColumnsHold(expandingWild) {
    this._clearExpandingWildHoldFx();
    if (!Array.isArray(expandingWild) || !expandingWild.length) return;

    const cfg = gameConfig.adapter;
    const animName = cfg.expandingWildSpineAnimation ?? 'win';
    const cw = gameConfig.cellWidth;
    const ch = gameConfig.cellHeight;
    const blockH = gameConfig.rowCount * ch;
    const widthScale = cfg.expandingWildWidthScale ?? 1;

    for (let reel = 0; reel < Math.min(expandingWild.length, this.reels.length); reel++) {
      const wildSymbolId = Number(expandingWild[reel]);
      if (!Number.isFinite(wildSymbolId) || wildSymbolId <= 0) continue;

      const targetReel = this.reels[reel];
      const centerX = targetReel.root.x + cw / 2;
      const centerY = blockH / 2;

      /** @type {import('./Symbol.js').Symbol[]} */
      const columnSymbols = [];
      for (let row = 0; row < gameConfig.rowCount; row++) {
        const sym = targetReel.getSymbolAtVisibleRow(row);
        if (sym) columnSymbols.push(sym);
      }

      const restoreVisibility = () => {
        for (const sym of columnSymbols) {
          sym.root.visible = true;
        }
      };
      for (const sym of columnSymbols) {
        sym.root.visible = false;
      }

      const spineFx = createExpandingWildSpineInstance(this.spineAssets);

      if (!spineFx) {
        for (let row = 0; row < gameConfig.rowCount; row++) {
          targetReel.setVisibleRowSymbolId(row, wildSymbolId);
        }
        restoreVisibility();
        continue;
      }

      holdExpandingWildSpineAtEnd(spineFx, animName);

      const columnRoot = new Container();
      columnRoot.eventMode = 'none';
      columnRoot.position.set(centerX, centerY);

      const { layer: columnBg, pulseTween: bgPulseTween, grad: columnBgGrad } = this._createExpandingWildColumnBg(cw, blockH, {
        pulse: false,
      });
      columnRoot.addChild(columnBg);

      spineFx.position.set(0, 0);
      if (widthScale !== 1) {
        spineFx.scale.x *= widthScale;
      }
      fitExpandingWildMultiplierToReel(spineFx);
      columnRoot.addChild(spineFx);
      const luxuryFx = this._attachExpandingWildLuxuryFx(columnRoot, cw, blockH);
      this.expandingWildFxLayer.addChild(columnRoot);

      this._expandingWildHoldRoots.push({
        columnRoot,
        bgPulseTween,
        columnBgGrad,
        luxuryFx,
        restoreVisibility,
      });
    }

    if (this._expandingWildHoldRoots.length && this.expandingWildFxLayer.parent === this.root) {
      this.root.setChildIndex(this.expandingWildFxLayer, this.root.children.length - 1);
    }
  }

  /**
   * @param {number[]} expandingWild per reel, 0 means none; >0 means wild symbol id
   * @param {{ turbo?: boolean, multipliers?: number[] }} [opts] Quick Spin — shorter reveal / hold, skip luxury frame FX. `multipliers` is the hub per-reel expanding-wild multiplier (2/3/5/10 pick `idle2x`…`idle10x` on the buy-bonus spine).
   */
  async animateExpandingWildColumns(expandingWild, opts = {}) {
    if (!Array.isArray(expandingWild) || !expandingWild.length) return;
    const turbo = !!opts.turbo;
    const wildReels = [];
    for (let reel = 0; reel < Math.min(expandingWild.length, this.reels.length); reel++) {
      const wildSymbolId = Number(expandingWild[reel]);
      if (!Number.isFinite(wildSymbolId) || wildSymbolId <= 0) continue;
      wildReels.push({ reel, wildSymbolId });
    }
    if (!wildReels.length) return;

    this._armWildReelMultipliers(opts.multipliers, expandingWild);

    sound.play('expandingWilds', { allowOverlap: true });
    this._cabinetJuice?.playExpandingWildImpact?.();

    const cfg = gameConfig.adapter;
    const expandScale = turbo
      ? Math.max(0.2, Number(gameConfig.turboExpandingWildExpandScale) || 0.45)
      : 1;
    const playScale = turbo
      ? Math.max(0.2, Number(gameConfig.turboExpandingWildPlayScale) || 0.4)
      : 1;
    const expandSec = (cfg.expandingWildExpandSec ?? 0.65) * expandScale;
    const revealStart = cfg.expandingWildRevealStartRatio ?? (gameConfig.cellHeight / (gameConfig.rowCount * gameConfig.cellHeight));
    const playSec = (cfg.expandingWildPlaySec ?? 4) * playScale;
    const spineTimeScale = turbo
      ? Math.max(1, Number(gameConfig.turboExpandingWildSpineTimeScale) || 2.8)
      : (cfg.expandingWildSpineTimeScale ?? 1);
    const multipliers = Array.isArray(opts.multipliers) ? opts.multipliers : null;
    const cw = gameConfig.cellWidth;
    const ch = gameConfig.cellHeight;
    const blockH = gameConfig.rowCount * ch;
    const widthScale = cfg.expandingWildWidthScale ?? 1;

    const cleanupColumnFx = (columnRoot, maskHost, bgPulseTween, luxuryFx, columnBgGrad) => {
      if (bgPulseTween) {
        bgPulseTween.kill();
      }
      luxuryFx?.stop();
      if (columnRoot?.parent) {
        columnRoot.mask = null;
        columnRoot.parent.removeChild(columnRoot);
        columnRoot.destroy({ children: true });
      }
      columnBgGrad?.destroy?.();
      if (maskHost?.parent) {
        maskHost.parent.removeChild(maskHost);
        maskHost.destroy({ children: true });
      }
    };

    /** @type {Array<{ columnRoot: Container, revealMaskHost: Container | null, spineFx: import('@esotericsoftware/spine-pixi-v8').Spine, animName: string, bgPulseTween: import('gsap').Tween | null, showColumnWilds: () => void }>} */
    const fxColumns = [];
    /** @type {Array<{ targetReel: import('./Reel.js').Reel, wildSymbolId: number, showColumnWilds: () => void }>} */
    const fallbackColumns = [];

    for (const { reel, wildSymbolId } of wildReels) {
      const targetReel = this.reels[reel];
      const reelX = targetReel.root.x;
      const centerX = reelX + cw / 2;
      const centerY = blockH / 2;

      /** @type {import('./Symbol.js').Symbol[]} */
      const columnSymbols = [];
      for (let row = 0; row < gameConfig.rowCount; row++) {
        const sym = targetReel.getSymbolAtVisibleRow(row);
        if (sym) columnSymbols.push(sym);
      }

      const hideColumnSymbols = () => {
        for (const sym of columnSymbols) {
          sym.root.visible = false;
        }
      };
      const showColumnWilds = () => {
        for (let row = 0; row < gameConfig.rowCount; row++) {
          targetReel.setVisibleRowSymbolId(row, wildSymbolId);
        }
        for (const sym of columnSymbols) {
          sym.root.visible = true;
        }
      };

      const spineFx = createExpandingWildSpineInstance(this.spineAssets);

      if (!spineFx) {
        hideColumnSymbols();
        fallbackColumns.push({ targetReel, wildSymbolId, showColumnWilds });
        continue;
      }

      hideColumnSymbols();

      const columnRoot = new Container();
      columnRoot.eventMode = 'none';
      columnRoot.position.set(centerX, centerY);

      const { layer: columnBg, pulseTween: bgPulseTween, grad: columnBgGrad } = this._createExpandingWildColumnBg(cw, blockH, {
        pulse: !turbo,
      });
      columnRoot.addChild(columnBg);

      spineFx.position.set(0, 0);
      if (widthScale !== 1) {
        spineFx.scale.x *= widthScale;
      }
      fitExpandingWildMultiplierToReel(spineFx);
      resetExpandingWildSpine(spineFx);
      const animName = resolveExpandingWildSpineAnimation(spineFx, multipliers ? multipliers[reel] : 0);
      playExpandingWildSpineAnimation(spineFx, animName, spineTimeScale);
      columnRoot.addChild(spineFx);

      const luxuryFx = turbo ? null : this._attachExpandingWildLuxuryFx(columnRoot, cw, blockH);

      /** @type {Container | null} */
      let revealMaskHost = null;
      if (expandSec > 0 && revealStart < 1) {
        const revealMaskW = cw * 1.5;
        const maskGfx = new Graphics();
        maskGfx.rect(-revealMaskW / 2, -blockH / 2, revealMaskW, blockH).fill(0xffffff);
        maskGfx.eventMode = 'none';

        revealMaskHost = new Container();
        revealMaskHost.eventMode = 'none';
        revealMaskHost.position.set(centerX, centerY);
        revealMaskHost.addChild(maskGfx);
        revealMaskHost.scale.set(1, revealStart);

        this.expandingWildFxLayer.addChild(revealMaskHost);
        columnRoot.mask = revealMaskHost;
      }

      this.expandingWildFxLayer.addChild(columnRoot);
      fxColumns.push({
        columnRoot,
        revealMaskHost,
        spineFx,
        animName,
        bgPulseTween,
        columnBgGrad,
        luxuryFx,
        showColumnWilds,
      });
    }

    if (this.expandingWildFxLayer.parent === this.root) {
      this.root.setChildIndex(this.expandingWildFxLayer, this.root.children.length - 1);
    }

    if (!fxColumns.length && !fallbackColumns.length) return;

    let spineAnimSec = 0;
    for (const col of fxColumns) {
      const anim = col.spineFx?.skeleton?.data?.findAnimation(col.animName);
      if (!anim) continue;
      const sec = (anim.duration ?? 0) / Math.max(0.01, spineTimeScale);
      if (sec > spineAnimSec) spineAnimSec = sec;
    }
    const holdSec = fxColumns.length ? Math.max(playSec, spineAnimSec) : playSec;

    await new Promise((resolve) => {
      let fxDone = fxColumns.length === 0;
      let fallbackPending = fallbackColumns.length;

      const checkDone = () => {
        if (fxDone && fallbackPending === 0) resolve();
      };

      if (fxColumns.length) {
        const masterTl = gsap.timeline({
          onComplete: () => {
            for (const col of fxColumns) {
              cleanupColumnFx(col.columnRoot, col.revealMaskHost, col.bgPulseTween, col.luxuryFx, col.columnBgGrad);
              col.showColumnWilds();
            }
            fxDone = true;
            checkDone();
          },
        });
        this.animationController.trackTween(masterTl);

        for (const col of fxColumns) {
          if (col.revealMaskHost) {
            masterTl.to(
              col.revealMaskHost.scale,
              {
                y: 1,
                duration: expandSec,
                ease: 'power2.out',
              },
              0,
            );
          }
        }

        masterTl.call(() => {
          for (const col of fxColumns) {
            col.columnRoot.mask = null;
            if (col.revealMaskHost?.parent) {
              col.revealMaskHost.parent.removeChild(col.revealMaskHost);
              col.revealMaskHost.destroy({ children: true });
              col.revealMaskHost = null;
            }
            playExpandingWildSpineAnimation(col.spineFx, col.animName, spineTimeScale);
          }
        });

        masterTl.to({}, { duration: holdSec });
      }

      for (const col of fallbackColumns) {
        this._playExpandingWildFallback(col.targetReel, col.wildSymbolId, () => {}, () => {
          col.showColumnWilds();
          fallbackPending -= 1;
          checkDone();
        });
      }

      checkDone();
    });
    this._syncWildMultiplierBadges();
  }

  /**
   * Store per-reel multipliers before the reels spin so badges can appear on landing wilds.
   * Does not paint yet — {@link #_syncWildMultiplierBadges} runs when each reel stops.
   * @param {number[] | null | undefined} multipliers
   * @param {number[] | null | undefined} expandingWild
   */
  armWildReelMultipliers(multipliers, expandingWild) {
    this._armWildReelMultipliers(multipliers, expandingWild);
  }

  /**
   * Remember which reels show a buy-bonus multiplier on their expanded wild.
   * @param {number[] | null | undefined} multipliers
   * @param {number[] | null | undefined} expandingWild
   */
  _armWildReelMultipliers(multipliers, expandingWild) {
    const count = this.reels.length;
    const next = new Array(count).fill(0);
    const ids = new Array(count).fill(0);
    const hasMult = Array.isArray(multipliers);
    for (let reel = 0; reel < count; reel++) {
      const wildId = Number(expandingWild?.[reel]);
      const n = hasMult ? Math.round(Number(multipliers[reel])) : 0;
      if (!Number.isFinite(wildId) || wildId <= 0) continue;
      if (n !== 2 && n !== 3 && n !== 5 && n !== 10) continue;
      next[reel] = n;
      ids[reel] = wildId;
    }
    this._wildReelMultipliers = next;
    this._wildReelSymbolIds = ids;
  }

  /**
   * @param {number[] | null | undefined} multipliers
   * @param {number[] | null | undefined} expandingWild per reel, 0 = none
   */
  setWildReelMultipliers(multipliers, expandingWild) {
    this._armWildReelMultipliers(multipliers, expandingWild);
    this._syncWildMultiplierBadges();
  }

  /**
   * Top-right badge on each wild whose reel has a 2/3/5/10 multiplier.
   * @param {{ col?: number, landing?: boolean }} [opts]
   *   `col` limits the update to one reel. `landing` uses the on-screen stop cell during the bounce.
   */
  _syncWildMultiplierBadges(opts = {}) {
    const mults = this._wildReelMultipliers;
    const ids = this._wildReelSymbolIds;
    const onlyCol = Number.isFinite(opts.col) ? opts.col : null;
    const useLanding = !!opts.landing;
    this.reels.forEach((reel, col) => {
      if (onlyCol != null && col !== onlyCol) return;
      const n = mults?.[col] ?? 0;
      const wildId = ids?.[col] ?? 0;
      for (let r = 0; r < gameConfig.rowCount; r++) {
        const sym = useLanding ? reel.getLandingSymbol(r) : reel.getSymbolAtVisibleRow(r);
        if (!sym) continue;
        if (n > 0 && wildId > 0 && sym.getSymbolId() === wildId) sym.setWildMultiplierBadge(n);
        else sym.clearWildMultiplierBadge();
      }
    });
  }

  /**
   * Symbol pop fallback when the expanding-wild spine is unavailable.
   * @param {import('./Reel.js').Reel} targetReel
   * @param {number} wildSymbolId
   * @param {() => void} onReveal
   * @param {() => void} onDone
   */
  _playExpandingWildFallback(targetReel, wildSymbolId, onReveal, onDone) {
    const tl = gsap.timeline({
      onComplete: onDone,
      onInterrupt: onDone,
    });
    this.animationController.trackTween(tl);
    for (let row = 0; row < gameConfig.rowCount; row++) {
      const sym = targetReel.getSymbolAtVisibleRow(row);
      tl.to(
        sym.root.scale,
        {
          x: 1.12,
          y: 1.12,
          duration: 0.09,
          ease: 'power2.out',
        },
        row * 0.05,
      ).add(() => {
        onReveal();
        targetReel.setVisibleRowSymbolId(row, wildSymbolId);
      });
      tl.to(sym.root.scale, {
        x: 1,
        y: 1,
        duration: 0.11,
        ease: 'power2.in',
      });
    }
  }

  /** Static payline win presentation for screenshots / debug (no cycling). */
  showStaticPaylineWin(paylineIndex) {
    const cells = this._fullPaylineCells(paylineIndex);
    if (!cells.length) return;
    this._applyGridWinPresentation(cells, { paylineIndex, displayMode: 'line' });
  }

  /** All visible reel cells sharp and idle — for photo / debug layouts. */
  showAllVisibleSymbolsSharp() {
    this._lowerAllLiftedSymbols();
    this.stopWinLineCycle();
    this.clearPaylineOverlay();
    for (const reel of this.reels) {
      reel.showVisibleWindowSharp();
    }
    this._applyReelsViewportMask(this._maskPadRelaxed);
  }

  /**
   * @param {number[][]} grid column-major [reel][row]
   */
  setGridInstant(grid) {
    this.reels.forEach((reel, col) => {
      const reelCol = grid[col];
      const colResult = Array.from(
        { length: gameConfig.rowCount },
        (_, row) => reelCol?.[row],
      );
      reel.setResultInstant(colResult);
    });
    this._applyReelsViewportMask(this._maskPadRelaxed);
  }

  /** Return lifted win symbols to their reels (only symbols on the lift layer). */
  _lowerAllLiftedSymbols() {
    for (const reel of this.reels) {
      for (const sym of reel._stripSymbols) {
        if (sym.root?.parent === this._symbolWinLiftLayer) {
          this._lowerSymbolFromWin(sym, reel);
        }
      }
    }
  }

  /**
   * One-shot cleanup when a new round starts: stop win cycling, overlays, tweens, and idle symbols.
   * Called from {@link SlotMachine#prepareForNewRound} before reels spin.
   */
  prepareForNewRound() {
    this._wildReelMultipliers = null;
    this._wildReelSymbolIds = null;
    this._clearExpandingWildHoldFx();
    this.stopWinLineCycle();
    this.clearAnticipationVisuals();
    this.clearPaylineOverlay();
    this._lowerAllLiftedSymbols();
    gsap.killTweensOf(this._symbolWinLiftLayer);
    for (const reel of this.reels) {
      reel.cancelActiveSpinAnimation?.();
      gsap.killTweensOf(reel.stripContainer);
      // resetForNewRound clears win artifacts on the visible pad; skip a second full-strip walk.
      reel.resetForNewRound();
    }
  }

  /** @deprecated Use {@link #prepareForNewRound} */
  clearWinState() {
    this.prepareForNewRound();
  }

  /**
   * Dim non-winners; lift winners; frame every cell on the full payline path.
   * @param {import('../logic/winEvaluator.js').CellRef[]} winCells
   * @param {{ paylineIndex?: number | null, displayMode?: string }} [opts]
   */
  _applyGridWinPresentation(winCells, opts = {}) {
    const winSet = new Set(winCells.map((c) => `${c.reel},${c.row}`));
    const isSymbolOnly = opts.displayMode === 'symbols';
    const linePathCells =
      !isSymbolOnly && opts.paylineIndex != null
        ? this._fullPaylineCells(opts.paylineIndex)
        : winCells;
    const lineSet = new Set(linePathCells.map((c) => `${c.reel},${c.row}`));
    const lineColor = this._paylineColor(opts.paylineIndex);

    this.reels.forEach((reel, col) => {
      for (let r = 0; r < gameConfig.rowCount; r++) {
        const sym = reel.getSymbolAtVisibleRow(r);
        if (!sym) continue;
        const key = `${col},${r}`;
        const isWin = winSet.has(key);
        const onLine = lineSet.has(key);

        sym.clearWinLineBorder();

        if (isWin) {
          sym.setWinPresentation('highlight');
          sym.showWinLineBorder(lineColor, { strong: true });
          this._liftSymbolForWin(sym, reel);
        } else if (onLine) {
          sym.setWinPresentation('lineIdle');
          sym.showWinLineBorder(lineColor, { strong: false });
          this._lowerSymbolFromWin(sym, reel);
        } else {
          sym.setWinPresentation('dim');
          this._lowerSymbolFromWin(sym, reel);
        }
      }
    });
    this._syncWildMultiplierBadges();
  }

  /**
   * @param {import('../logic/winEvaluator.js').WinResult['hits']} hits
   */
  _applyGridWinPresentationForHits(hits) {
    /** @type {Map<string, { color: number, strong: boolean }>} */
    const borderByCell = new Map();
    const winSet = new Set();

    for (const hit of hits) {
      for (const c of hit.cells ?? []) {
        winSet.add(`${c.reel},${c.row}`);
      }

      if (hit.displayMode === 'symbols') {
        const color = this._paylineColor(null);
        for (const c of hit.cells ?? []) {
          const key = `${c.reel},${c.row}`;
          borderByCell.set(key, { color, strong: true });
        }
        continue;
      }

      const color = this._paylineColor(hit.paylineIndex);
      const fullLine =
        hit.paylineIndex != null ? this._fullPaylineCells(hit.paylineIndex) : hit.cells ?? [];
      const hitWinSet = new Set((hit.cells ?? []).map((c) => `${c.reel},${c.row}`));

      for (const c of fullLine) {
        const key = `${c.reel},${c.row}`;
        const strong = hitWinSet.has(key);
        const prev = borderByCell.get(key);
        if (!prev || (strong && !prev.strong)) {
          borderByCell.set(key, { color, strong: strong || !!prev?.strong });
        }
      }
    }

    this.reels.forEach((reel, col) => {
      for (let r = 0; r < gameConfig.rowCount; r++) {
        const sym = reel.getSymbolAtVisibleRow(r);
        if (!sym) continue;
        const key = `${col},${r}`;
        const border = borderByCell.get(key);
        const isWin = winSet.has(key);

        sym.clearWinLineBorder();

        if (isWin) {
          sym.setWinPresentation('highlight');
          if (border) sym.showWinLineBorder(border.color, { strong: border.strong });
          this._liftSymbolForWin(sym, reel);
        } else if (border) {
          sym.setWinPresentation('lineIdle');
          sym.showWinLineBorder(border.color, { strong: border.strong });
          this._lowerSymbolFromWin(sym, reel);
        } else {
          sym.setWinPresentation('dim');
          this._lowerSymbolFromWin(sym, reel);
        }
      }
    });
    this._syncWildMultiplierBadges();
  }

  /**
   * Restart every cell’s win clip from frame 0 (shared symbols included) so a payline phase stays in sync.
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   */
  _triggerWinClipsForCells(cells, opts = {}) {
    const unique = this._uniqueWinCells(cells);
    if (!unique.length) return;

    unique.sort((a, b) => a.reel - b.reel || a.row - b.row);
    const stagger =
      opts.staggerSec != null && Number.isFinite(opts.staggerSec)
        ? opts.staggerSec
        : (gameConfig.adapter.winStaggerSec ?? 0);
    const symAt = (c) => this.reels[c.reel].getSymbolAtVisibleRow(c.row);
    const play = (c) => symAt(c)?.playWinOnce?.();

    if (stagger <= 0 || unique.length < 2) {
      for (const c of unique) play(c);
      return;
    }

    const tl = this.animationController.createTimeline();
    unique.forEach((c, i) => {
      tl.call(() => play(c), null, i * stagger);
    });
  }

  /**
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   * @param {{ staggerSec?: number }} [opts]
   */
  _startWinClips(cells, opts = {}) {
    this._triggerWinClipsForCells(cells, opts);
  }

  /**
   * One payline: play each symbol’s `win` once from the start, then resolve when the full line finishes.
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   * @param {{ staggerSec?: number }} [opts]
   * @returns {Promise<void>}
   */
  _playLineWinClips(cells, opts = {}) {
    return new Promise((resolve) => {
      const unique = this._uniqueWinCells(cells);
      if (!unique.length) {
        resolve();
        return;
      }

      this._triggerWinClipsForCells(cells, opts);

      const totalSec = this._lineWinPlaybackSec(cells, opts);
      this._winLineCycleResolve = resolve;
      const wait = gsap.delayedCall(totalSec, () => {
        this._winLineCycleDelay = null;
        this._winLineCycleResolve = null;
        this.animationController.releaseTween(wait);
        resolve();
      });
      this._winLineCycleDelay = wait;
      this.animationController.trackTween(wait);
    });
  }

  /**
   * For debug overlay: label positions in this container's space.
   * @returns {Array<{ x: number, y: number, text: string }>}
   */
  getDebugCellLabels() {
    const out = [];
    const step = gameConfig.cellWidth + gameConfig.reelGap;
    const ch = gameConfig.cellHeight;
    for (let col = 0; col < gameConfig.reelCount; col++) {
      const reel = this.reels[col];
      const li = reel.getLandIndex();
      for (let row = 0; row < gameConfig.rowCount; row++) {
        out.push({
          x: col * step + 4,
          y: row * ch + 4,
          text: `R${col}↕${li + row}`,
        });
      }
    }
    return out;
  }
}
